import { z } from "zod";
import type { MessageScope } from "./agentSdk";

export const EMAIL_PROVIDER = {
  AGENT_EMAIL: "agent_email",
  FORGEPOINT_MAIL: "forgepoint_mail",
  GMAIL: "gmail",
  MICROSOFT_OUTLOOK: "microsoft_outlook",
  SMTP: "smtp",
} as const;

export const EMAIL_PROVIDER_VALUES = [
  EMAIL_PROVIDER.AGENT_EMAIL,
  EMAIL_PROVIDER.FORGEPOINT_MAIL,
  EMAIL_PROVIDER.GMAIL,
  EMAIL_PROVIDER.MICROSOFT_OUTLOOK,
  EMAIL_PROVIDER.SMTP,
] as const;

export type EmailProvider = (typeof EMAIL_PROVIDER_VALUES)[number];

export const QUOTE_ITEM_CREATION_POLICY = {
  ALLOW_CREATE_AND_UPDATE: "allow_create_and_update",
  ALLOW_CREATE_UPDATE_AND_MATERIALS: "allow_create_update_and_materials",
  PRICE_MODEL_ONLY: "price_model_only",
  NO_CREATE_OR_UPDATE: "no_create_or_update",
} as const;

export const QUOTE_ITEM_CREATION_POLICY_VALUES = [
  QUOTE_ITEM_CREATION_POLICY.ALLOW_CREATE_AND_UPDATE,
  QUOTE_ITEM_CREATION_POLICY.ALLOW_CREATE_UPDATE_AND_MATERIALS,
  QUOTE_ITEM_CREATION_POLICY.PRICE_MODEL_ONLY,
  QUOTE_ITEM_CREATION_POLICY.NO_CREATE_OR_UPDATE,
] as const;

export type ItemCreationPolicy =
  (typeof QUOTE_ITEM_CREATION_POLICY_VALUES)[number];

/** Controls how configured quote reviewers are assigned to final review tasks. */
export const QUOTE_REVIEWER_ASSIGNMENT_MODE = {
  ALL: "all",
  ONE_OF: "one_of",
} as const;

export const QUOTE_REVIEWER_ASSIGNMENT_MODE_VALUES = [
  QUOTE_REVIEWER_ASSIGNMENT_MODE.ALL,
  QUOTE_REVIEWER_ASSIGNMENT_MODE.ONE_OF,
] as const;

/** Quote reviewer assignment requirement for final quote review tasks. */
export type QuoteReviewerAssignmentMode =
  (typeof QUOTE_REVIEWER_ASSIGNMENT_MODE_VALUES)[number];

/**
 * How far out the agent may communicate in a given direction, along a hierarchy
 * where USER ⊂ INTERNAL ⊂ EXTERNAL (USER is most restrictive: only parties with
 * an org user account; INTERNAL adds parties on an internal email domain;
 * EXTERNAL adds everyone else). This is the policy-side counterpart to a party's
 * resolved `MessageScope`: a party is permitted when its scope falls within the
 * configured reach.
 */
export const COMMUNICATION_SCOPE_VALUES = [
  "USER",
  "INTERNAL",
  "EXTERNAL",
] as const;
export const CommunicationScopeEnum = z.enum(COMMUNICATION_SCOPE_VALUES);
export type CommunicationScope = z.infer<typeof CommunicationScopeEnum>;
export const COMMUNICATION_SCOPE = CommunicationScopeEnum.enum;

/** Which outbound agent emails must be approved by a human before they are sent. */
export const OUTBOUND_APPROVAL_VALUES = ["NONE", "EXTERNAL"] as const;
export const OutboundApprovalEnum = z.enum(OUTBOUND_APPROVAL_VALUES);
export type OutboundApproval = z.infer<typeof OutboundApprovalEnum>;
export const OUTBOUND_APPROVAL = OutboundApprovalEnum.enum;

/**
 * Per-agent policy for how far out the agent may communicate in each direction.
 * The two directions are configured independently; each is the furthest-out
 * party scope the agent may exchange messages with.
 */
export const ExternalCommunicationConfigSchema = z.object({
  /** Furthest-out scope the agent may receive messages from. */
  inbound: CommunicationScopeEnum,
  /** Furthest-out scope the agent may send messages to. */
  outbound: CommunicationScopeEnum,
  outboundApproval: OutboundApprovalEnum.optional(),
});

/** Per-agent inbound/outbound communication reach policy. */
export type ExternalCommunicationConfig = z.infer<
  typeof ExternalCommunicationConfigSchema
>;

/** Default communication config when no agent settings record exists yet (most permissive). */
export const DEFAULT_EXTERNAL_COMMUNICATION_CONFIG: ExternalCommunicationConfig =
  {
    inbound: COMMUNICATION_SCOPE.EXTERNAL,
    outbound: COMMUNICATION_SCOPE.EXTERNAL,
  };

/** Reach hierarchy rank; a party is within a reach when its rank is <= the reach's rank. */
const SCOPE_RANK: Record<CommunicationScope, number> = {
  [COMMUNICATION_SCOPE.USER]: 0,
  [COMMUNICATION_SCOPE.INTERNAL]: 1,
  [COMMUNICATION_SCOPE.EXTERNAL]: 2,
};

/**
 * Whether a party at `partyScope` falls within the given `reach`.
 * @param reach - The furthest-out scope allowed for the direction
 * @param partyScope - The resolved scope of the party (USER/INTERNAL/EXTERNAL)
 * @returns True when the party is permitted by the reach
 */
export function isScopeWithinReach(
  reach: CommunicationScope,
  partyScope: MessageScope,
): boolean {
  return SCOPE_RANK[partyScope] <= SCOPE_RANK[reach];
}

/**
 * Whether the agent may send to external recipients.
 * @param config - The communication config
 * @returns True when outbound reach extends to external parties
 */
export function isExternalOutboundEnabled(
  config: ExternalCommunicationConfig,
): boolean {
  return config.outbound === COMMUNICATION_SCOPE.EXTERNAL;
}

/**
 * Whether every agent email to an external recipient must be approved by a human first.
 * @param config - The communication config
 * @returns True when outbound reach is external and external approval is configured
 */
export function isExternalOutboundApprovalRequired(
  config: ExternalCommunicationConfig,
): boolean {
  return (
    config.outbound === COMMUNICATION_SCOPE.EXTERNAL &&
    config.outboundApproval === OUTBOUND_APPROVAL.EXTERNAL
  );
}

/**
 * Whether the agent may receive from external senders.
 * @param config - The communication config
 * @returns True when inbound reach extends to external parties
 */
export function isExternalInboundEnabled(
  config: ExternalCommunicationConfig,
): boolean {
  return config.inbound === COMMUNICATION_SCOPE.EXTERNAL;
}

/** Schema for item search sub-agent configuration */
export const ItemSearchConfigSchema = z.object({
  enabled: z.boolean(),
  searchStrategy: z.string(),
});

/** Configuration for the item search sub-agent */
export type ItemSearchConfig = z.infer<typeof ItemSearchConfigSchema>;

export const StandardAgentSettingsSchema = z.object({
  id: z.string(),
  organizationId: z.string(),
  agentName: z.string(),
  timezone: z.string().nullable(),
  internalEmailDomains: z.array(z.string()),
  externalCommunicationConfig: ExternalCommunicationConfigSchema,
  enableSetupSkill: z.boolean(),
  enableDrawingTo3d: z.boolean(),
  enableOpportunityThreadCreation: z.boolean(),
  enableFollowUps: z.boolean(),
  notifyTeamOnWon: z.boolean(),
  enableExternalFollowUps: z.boolean(),
  externalFollowUpIntervalDays: z.array(z.number().int().min(1)),
  maxExternalFollowUps: z.number().int().min(1).max(10),
  informationCollectionRequirements: z.string(),
  secondaryInformationToRequest: z.string(),
  doesRequireTechnicalDrawings: z.boolean(),
  sendQuoteEmailToCustomer: z.boolean(),
  quotingSop: z.string(),
  itemNamingGuidance: z.string().nullable(),
  enableDemandForecasting: z.boolean(),
  demandForecastSop: z.string(),
  ccEmails: z.array(z.string()),
  extractionReviewSchema: z.string().nullable(),
  quoteReviewerUserIds: z.array(z.string()),
  quoteReviewerAssignmentMode: z.enum(QUOTE_REVIEWER_ASSIGNMENT_MODE_VALUES),
  forgepointEmailPrefix: z.string().nullable(),
  microsoftOutlookConnectionId: z.string().nullable(),
  microsoftOutlookSharedFolderEmail: z.string().nullable(),
  microsoftOutlookSharedFolderId: z.string().nullable(),
  emailProvider: z.enum(EMAIL_PROVIDER_VALUES).nullable(),
  agentEmailConnectionId: z.string().nullable(),
  agentEmailFromAddress: z.string().nullable(),
  gmailConnectionId: z.string().nullable(),
  gmailFromEmail: z.string().nullable(),
  smtpConnectionId: z.string().nullable(),
  smtpReplyLookupImapConnectionId: z.string().nullable(),
  fromPhoneId: z.string().nullable(),
  instantMessengerConnectionId: z.string().nullable(),
  escalationUserIds: z.array(z.string()),
  quoteItemCreationPolicy: z.enum(QUOTE_ITEM_CREATION_POLICY_VALUES),
  enableRFQExtraction: z.boolean(),
  itemSearchConfig: ItemSearchConfigSchema.nullable(),
  sopVersion: z.string().nullable(),
  sopUpdatedBy: z.string().nullable(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
});

export type StandardAgentSettings = z.infer<typeof StandardAgentSettingsSchema>;

export const StandardAgentSettingsResponseSchema = z.object({
  settings: StandardAgentSettingsSchema.nullable(),
});

const OptionalStringSchema = z.string().optional();
const OptionalBooleanSchema = z.boolean().optional();
const OptionalStringArraySchema = z.array(z.string()).optional();
const NullableOptionalStringSchema = z.string().nullable().optional();

export const UpsertStandardAgentSettingsBodySchema = z.object({
  agentName: OptionalStringSchema,
  timezone: NullableOptionalStringSchema,
  internalEmailDomains: OptionalStringArraySchema,
  externalCommunicationConfig: ExternalCommunicationConfigSchema.optional(),
  enableSetupSkill: OptionalBooleanSchema,
  enableDrawingTo3d: OptionalBooleanSchema,
  enableOpportunityThreadCreation: OptionalBooleanSchema,
  enableFollowUps: OptionalBooleanSchema,
  notifyTeamOnWon: OptionalBooleanSchema,
  enableExternalFollowUps: OptionalBooleanSchema,
  externalFollowUpIntervalDays: z.array(z.number().int().min(1)).optional(),
  maxExternalFollowUps: z.number().int().min(1).max(10).optional(),
  informationCollectionRequirements: OptionalStringSchema,
  secondaryInformationToRequest: OptionalStringSchema,
  doesRequireTechnicalDrawings: OptionalBooleanSchema,
  sendQuoteEmailToCustomer: OptionalBooleanSchema,
  quotingSop: OptionalStringSchema,
  itemNamingGuidance: NullableOptionalStringSchema,
  enableDemandForecasting: OptionalBooleanSchema,
  demandForecastSop: OptionalStringSchema,
  ccEmails: OptionalStringArraySchema,
  extractionReviewSchema: NullableOptionalStringSchema,
  quoteReviewerUserIds: OptionalStringArraySchema,
  quoteReviewerAssignmentMode: z
    .enum(QUOTE_REVIEWER_ASSIGNMENT_MODE_VALUES)
    .optional(),
  forgepointEmailPrefix: NullableOptionalStringSchema,
  microsoftOutlookConnectionId: NullableOptionalStringSchema,
  microsoftOutlookSharedFolderEmail: NullableOptionalStringSchema,
  microsoftOutlookSharedFolderId: NullableOptionalStringSchema,
  emailProvider: z.enum(EMAIL_PROVIDER_VALUES).nullable().optional(),
  agentEmailConnectionId: NullableOptionalStringSchema,
  agentEmailFromAddress: NullableOptionalStringSchema,
  gmailConnectionId: NullableOptionalStringSchema,
  gmailFromEmail: NullableOptionalStringSchema,
  smtpConnectionId: NullableOptionalStringSchema,
  smtpReplyLookupImapConnectionId: NullableOptionalStringSchema,
  fromPhoneId: NullableOptionalStringSchema,
  instantMessengerConnectionId: NullableOptionalStringSchema,
  escalationUserIds: OptionalStringArraySchema,
  quoteItemCreationPolicy: z.enum(QUOTE_ITEM_CREATION_POLICY_VALUES).optional(),
  enableRFQExtraction: OptionalBooleanSchema,
  itemSearchConfig: ItemSearchConfigSchema.nullable().optional(),
  sopVersion: NullableOptionalStringSchema,
  sopUpdatedBy: NullableOptionalStringSchema,
});

export type UpsertStandardAgentSettingsBody = z.infer<
  typeof UpsertStandardAgentSettingsBodySchema
>;
